import { createSelector } from '@reduxjs/toolkit';
import { uniqBy } from 'lodash';

import { memberDetailsSel } from '@modules/leadershipPositions/duck/selectors';
import { childrenSel } from '@modules/parentGuardian/duck/selectors';
import type { NormalizedReportItem } from '@modules/reports/basicReports/duck/utils';
import { emptyArr } from '@modules/shared/constants';
import {
  isParentOrYouthMemberSel,
  organizationGuidSel,
  packRosterItemsSel,
  unitTypeIdSel,
} from '@shared/duck/selectors/general.selectors';

import type { RootState } from '../../../../root/store';
import { basicReportsModuleSel } from '../../common/selectors';
import { validReports } from '../constants';

export const allReportsSel = (state: RootState) =>
  basicReportsModuleSel(state).reports;

export const allOrgReportsSel = createSelector(
  allReportsSel,
  organizationGuidSel,
  (allReports, organizationGuid: string) =>
    allReports[organizationGuid] ||
    (emptyArr as unknown as Array<NormalizedReportItem>),
);
/**
 * Used to list reports for Units in "Reports" tab
 */
export const reportsSel = createSelector(
  allOrgReportsSel,
  unitTypeIdSel,
  (reports, unitTypeId: number) => {
    const validReportCodes: string[] =
      validReports[Number(unitTypeId) as keyof typeof validReports];

    if (!validReportCodes) {
      return reports as NormalizedReportItem[];
    }

    const result = reports.filter(({ reportCode }) =>
      validReportCodes.includes(reportCode),
    );

    return result;
  },
);

export const allUserReportsSel = createSelector(
  allReportsSel,
  memberDetailsSel,
  isParentOrYouthMemberSel,
  (allReports, memberDetails, isParentOrYouthMember) => {
    const empty = emptyArr as unknown as Array<NormalizedReportItem>;
    if (isParentOrYouthMember) {
      const { profile: { personGuid } = {} } = memberDetails || {};
      return allReports[personGuid] || empty;
    }
    return empty;
  },
);

export const reportByCodeSel = createSelector(
  (_state: RootState, props: { reportCode: string }) => props.reportCode,
  allOrgReportsSel,
  (reportCode, reports) =>
    reports.find(report => report.reportCode === reportCode),
);

export const reportsByCodesSel = (reportCodes: string[] | null) =>
  createSelector(allOrgReportsSel, reports => {
    if (Array.isArray(reportCodes)) {
      return reports.filter(({ reportCode }) =>
        reportCodes.includes(reportCode),
      );
    }
    return null;
  });

export const selectYouthModalOpenSel = createSelector(
  basicReportsModuleSel,
  ({ selectYouthModalOpen }) => selectYouthModalOpen,
);

export const selectedReportCodeSel = createSelector(
  basicReportsModuleSel,
  ({ selectedReportCode }) => selectedReportCode,
);

export const selectedReportSel = createSelector(
  selectedReportCodeSel,
  reportsSel,
  allUserReportsSel,
  (selectedCode, reports, allUserReports) =>
    [...reports, ...allUserReports].find(
      ({ reportCode }) => selectedCode == reportCode,
    ) || ({} as NormalizedReportItem),
);
export const availableYouthsForReportSel = createSelector(
  packRosterItemsSel,
  childrenSel,
  (packRoster, pgChildren) => {
    const rosterItems = packRoster.filter(({ isAdult }) => !isAdult);

    return uniqBy([...rosterItems, ...pgChildren], 'personGuid');
  },
);
